import React, { useCallback, useState } from "react";
import type { IconType } from "react-icons";
import {
  FiAlertCircle as AlertCircle,
  FiDollarSign as Banknote,
  FiBell as Bell,
  FiHome as Building2,
  FiCalendar as Calendar,
  FiCheck as Check,
  FiChevronDown as ChevronDown,
  FiChevronLeft as ChevronLeft,
  FiChevronRight as ChevronRight,
  FiClock as Clock,
  FiCopy as Copy,
  FiExternalLink as ExternalLink,
  FiEye as Eye,
  FiEyeOff as EyeOff,
  FiFileText as FileText,
  FiFilter as Filter,
  FiHome as Home,
  FiImage as Image,
  FiInfo as Info,
  FiGrid as LayoutGrid,
  FiList as List,
  FiLoader as Loader2,
  FiLock as Lock,
  FiLogOut as LogOut,
  FiMail as Mail,
  FiMapPin as MapPin,
  FiMenu as Menu,
  FiMoreHorizontal as MoreHorizontal,
  FiEdit2 as Pencil,
  FiPhone as Phone,
  FiPlus as Plus,
  FiSave as Save,
  FiSearch as Search,
  FiSettings as Settings,
  FiTrash2 as Trash2,
  FiUpload as Upload,
  FiUser as User,
  FiUsers as Users,
  FiX as X,
} from "react-icons/fi";
import { PaginationBar } from "../../components/PaginationBar";
import WizardSteps from "../../components/WizardSteps";
import SearchBarToHeader from "./components/SearchBarToHeader";
import {
  EDIT_STEPS,
  type EditPropertyStepId,
} from "../EditProperty/components/stepConstants";
import { useToast } from "../../hooks/toast";
import {
  FLOW_BUTTON_SOLID_SHELL,
  FLOW_FORM_FIELD_LABEL_BASE,
  FLOW_FORM_HELPER_TEXT_BASE,
  FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS,
  FLOW_ORANGE_ACTION_BUTTON_BASE,
  FLOW_PRIMARY_BUTTON_BASE,
  FLOW_SAVE_BUTTON_BASE,
  FLOW_TEXT_INPUT_BASE,
  FLOW_TEXTAREA_BASE,
} from "../../styles/twDesignPrimitives";

function cn(...parts: (string | undefined | false)[]): string {
  return parts.filter(Boolean).join(" ");
}

export const VISUAL_TOKENS = {
  colors: {
    background: "#F6F7F9",
    text: "#111827",
    primary: "#4a90e2",
    secondary: "#DD6435",
    success: "#4CAF50",
    error: "#f44336",
    inputBorder: "#D1D5DB",
    inputFocus: "#2563EB",
    cardBorder: "#D1D5DB",
    muted: "#6B7280",
    flowSave: "#00BF33",
    flowAction: "#DD6435",
    flowNav: "#6EA5FF",
    formFieldBorder: "#E2E8F0",
    formFieldBg: "#FFFFFF",
    formControlText: "#4A5568",
    formControlAccent: "#3182CE",
  },
  typography: {
    fontFamilySans:
      'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    scale: {
      xs: "0.75rem",
      sm: "0.875rem",
      base: "1rem",
      lg: "1.125rem",
      xl: "1.25rem",
      "2xl": "1.5rem",
    },
  },
  spacing: {
    section: "2rem",
    stack: "1rem",
    inline: "0.75rem",
  },
} as const;

type IconCatalogEntry = {
  name: string;
  Icon: IconType;
};

const RADIUS_CATALOG: { token: string; usage: string }[] = [
  { token: "rounded-md", usage: "Campos, botões, cartões pequenos (6px)" },
  { token: "rounded-lg", usage: "Cartões, agrupamentos (8px)" },
  { token: "rounded-xl", usage: "Painéis destacados (12px)" },
  { token: "rounded-full", usage: "Indicadores de passo, avatares, etiquetas" },
];

const LUCIDE_ICON_CATALOG: IconCatalogEntry[] = [
  { name: "Search", Icon: Search },
  { name: "Plus", Icon: Plus },
  { name: "X", Icon: X },
  { name: "Check", Icon: Check },
  { name: "Loader2", Icon: Loader2 },
  { name: "Mail", Icon: Mail },
  { name: "User", Icon: User },
  { name: "Users", Icon: Users },
  { name: "Settings", Icon: Settings },
  { name: "ChevronDown", Icon: ChevronDown },
  { name: "ChevronRight", Icon: ChevronRight },
  { name: "ChevronLeft", Icon: ChevronLeft },
  { name: "Trash2", Icon: Trash2 },
  { name: "Pencil", Icon: Pencil },
  { name: "Calendar", Icon: Calendar },
  { name: "Clock", Icon: Clock },
  { name: "Home", Icon: Home },
  { name: "FileText", Icon: FileText },
  { name: "Upload", Icon: Upload },
  { name: "Image", Icon: Image },
  { name: "MapPin", Icon: MapPin },
  { name: "Phone", Icon: Phone },
  { name: "AlertCircle", Icon: AlertCircle },
  { name: "Info", Icon: Info },
  { name: "Menu", Icon: Menu },
  { name: "Filter", Icon: Filter },
  { name: "Bell", Icon: Bell },
  { name: "Lock", Icon: Lock },
  { name: "Eye", Icon: Eye },
  { name: "EyeOff", Icon: EyeOff },
  { name: "ExternalLink", Icon: ExternalLink },
  { name: "Copy", Icon: Copy },
  { name: "Save", Icon: Save },
  { name: "LayoutGrid", Icon: LayoutGrid },
  { name: "List", Icon: List },
  { name: "MoreHorizontal", Icon: MoreHorizontal },
  { name: "LogOut", Icon: LogOut },
  { name: "Building2", Icon: Building2 },
  { name: "Banknote", Icon: Banknote },
];

const COLOR_SWATCH_LABELS: Record<keyof typeof VISUAL_TOKENS.colors, string> = {
  background: "Fundo",
  text: "Texto",
  primary: "Primária",
  secondary: "Secundária",
  success: "Sucesso",
  error: "Erro",
  inputBorder: "Borda do campo",
  inputFocus: "Foco do campo",
  cardBorder: "Borda do cartão",
  muted: "Texto secundário",
  flowSave: "Fluxo · Guardar",
  flowAction: "Fluxo · Ação (laranja)",
  flowNav: "Fluxo · Navegação",
  formFieldBorder: "Campo · borda",
  formFieldBg: "Campo · fundo",
  formControlText: "Campo · texto",
  formControlAccent: "Campo · acento",
};

export type StyleGuideComponentVariant = "save" | "action" | "nav";
export type StyleGuideComponentSize = "sm" | "md" | "lg";

export interface StyleGuideButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: StyleGuideComponentVariant;
  size?: StyleGuideComponentSize;
  loading?: boolean;
  fullWidth?: boolean;
}

const buttonVariantClasses: Record<StyleGuideComponentVariant, string> = {
  save: FLOW_SAVE_BUTTON_BASE,
  action: FLOW_ORANGE_ACTION_BUTTON_BASE,
  nav: FLOW_PRIMARY_BUTTON_BASE,
};

const flowSolidButtonSizeClasses: Record<StyleGuideComponentSize, string> = {
  sm: "gap-1.5 px-3 py-2 text-xs leading-tight",
  md: "gap-1.5 px-4 py-3 text-sm leading-tight",
  lg: "gap-2 px-5 py-3.5 text-sm leading-tight",
};

const flowSolidChildSvgClasses =
  "[&_svg]:h-[18px] [&_svg]:w-[18px] [&_svg]:shrink-0 [&_svg]:text-white";

export const StyleGuideButton = React.forwardRef<
  HTMLButtonElement,
  StyleGuideButtonProps
>(
  (
    {
      variant = "nav",
      size = "md",
      loading = false,
      fullWidth = false,
      className,
      disabled,
      children,
      type = "button",
      ...rest
    },
    ref
  ) => {
    const isDisabled = Boolean(disabled || loading);
    const sizeClass = flowSolidButtonSizeClasses[size];
    return (
      <button
        ref={ref}
        {...rest}
        type={type}
        disabled={isDisabled}
        className={cn(
          "select-none",
          buttonVariantClasses[variant],
          sizeClass,
          flowSolidChildSvgClasses,
          fullWidth && "w-full",
          className
        )}
      >
        {loading ? (
          <Loader2 className="h-[1em] w-[1em] shrink-0 animate-spin text-white opacity-80" />
        ) : null}
        {children}
      </button>
    );
  }
);
StyleGuideButton.displayName = "StyleGuideButton";

export type StyleGuideInputValidationState = "default" | "error" | "success";

export interface StyleGuideInputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size"> {
  inputSize?: StyleGuideComponentSize;
  label?: string;
  helperText?: string;
  validationState?: StyleGuideInputValidationState;
  leftIcon?: React.ReactNode;
}

const inputSizeClasses: Record<StyleGuideComponentSize, string> = {
  sm: "!py-1.5 text-xs",
  md: "",
  lg: "!py-3 text-base",
};

const textareaSizeClasses: Record<StyleGuideComponentSize, string> = {
  sm: "!min-h-[72px] !py-1.5 text-xs",
  md: "",
  lg: "!min-h-[120px] !py-3 text-base",
};

const inputStateRing: Record<StyleGuideInputValidationState, string> = {
  default: "",
  error:
    "border-rose-400 focus:border-rose-500 focus:ring-2 focus:ring-rose-200/90 focus:ring-offset-0 focus-visible:border-rose-500 focus-visible:ring-2 focus-visible:ring-rose-200/90 focus-visible:ring-offset-0",
  success:
    "border-emerald-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200/90 focus:ring-offset-0 focus-visible:border-emerald-500 focus-visible:ring-2 focus-visible:ring-emerald-200/90 focus-visible:ring-offset-0",
};

export const StyleGuideInput = React.forwardRef<
  HTMLInputElement,
  StyleGuideInputProps
>(
  (
    {
      inputSize = "md",
      label,
      helperText,
      validationState = "default",
      leftIcon,
      className,
      id,
      disabled,
      ...rest
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    return (
      <div className={cn("flex w-full flex-col gap-2", className)}>
        {label ? (
          <label htmlFor={inputId} className={FLOW_FORM_FIELD_LABEL_BASE}>
            {label}
          </label>
        ) : null}
        <div className="relative flex w-full items-stretch">
          {leftIcon ? (
            <span className="pointer-events-none absolute left-3 top-1/2 z-[1] flex -translate-y-1/2 text-slate-400 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:stroke-[1.5]">
              {leftIcon}
            </span>
          ) : null}
          <input
            ref={ref}
            id={inputId}
            disabled={disabled}
            {...rest}
            aria-invalid={validationState === "error" ? true : undefined}
            className={cn(
              FLOW_TEXT_INPUT_BASE,
              inputSizeClasses[inputSize],
              leftIcon ? "pl-9" : "",
              validationState !== "default" && inputStateRing[validationState],
              rest.className
            )}
          />
        </div>
        {helperText ? (
          <p
            className={cn(
              FLOW_FORM_HELPER_TEXT_BASE,
              validationState === "error" && "not-italic text-rose-600",
              validationState === "success" && "not-italic text-emerald-700"
            )}
          >
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);
StyleGuideInput.displayName = "StyleGuideInput";

export interface StyleGuideTextareaProps
  extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, "size"> {
  inputSize?: StyleGuideComponentSize;
  label?: string;
  helperText?: string;
  validationState?: StyleGuideInputValidationState;
}

export const StyleGuideTextarea = React.forwardRef<
  HTMLTextAreaElement,
  StyleGuideTextareaProps
>(
  (
    {
      inputSize = "md",
      label,
      helperText,
      validationState = "default",
      className,
      id,
      disabled,
      ...rest
    },
    ref
  ) => {
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    return (
      <div className={cn("flex w-full flex-col gap-2", className)}>
        {label ? (
          <label htmlFor={inputId} className={FLOW_FORM_FIELD_LABEL_BASE}>
            {label}
          </label>
        ) : null}
        <textarea
          ref={ref}
          id={inputId}
          disabled={disabled}
          {...rest}
          aria-invalid={validationState === "error" ? true : undefined}
          className={cn(
            FLOW_TEXTAREA_BASE,
            textareaSizeClasses[inputSize],
            validationState !== "default" && inputStateRing[validationState]
          )}
        />
        {helperText ? (
          <p
            className={cn(
              FLOW_FORM_HELPER_TEXT_BASE,
              validationState === "error" && "not-italic text-rose-600",
              validationState === "success" && "not-italic text-emerald-700"
            )}
          >
            {helperText}
          </p>
        ) : null}
      </div>
    );
  }
);
StyleGuideTextarea.displayName = "StyleGuideTextarea";

export interface StyleGuideSearchBarProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size" | "type"> {
  barSize?: StyleGuideComponentSize;
  showSubmitButton?: boolean;
  submitLabel?: string;
  submitLoading?: boolean;
  onSearch?: () => void;
  formClassName?: string;
}

const searchBarTrackClasses: Record<StyleGuideComponentSize, string> = {
  sm: "h-10 py-0 pl-9 text-sm leading-10",
  md: "h-11 py-0 pl-10 text-sm leading-[44px]",
  lg: "h-12 py-0 pl-10 text-sm leading-[48px]",
};

const searchBarIconLayout: Record<StyleGuideComponentSize, string> = {
  sm: "left-2.5 [&_svg]:h-3.5 [&_svg]:w-3.5 [&_svg]:stroke-[1.5]",
  md: "left-3 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:stroke-[1.5]",
  lg: "left-3 [&_svg]:h-4 [&_svg]:w-4 [&_svg]:stroke-[1.5]",
};

const searchBarSubmitSize: Record<
  StyleGuideComponentSize,
  StyleGuideComponentSize
> = {
  sm: "sm",
  md: "sm",
  lg: "md",
};

export const StyleGuideSearchBar = React.forwardRef<
  HTMLInputElement,
  StyleGuideSearchBarProps
>(
  (
    {
      barSize = "md",
      showSubmitButton = true,
      submitLabel = "Buscar",
      submitLoading = false,
      onSearch,
      formClassName,
      className,
      disabled,
      placeholder = "Pesquisar…",
      ...inputProps
    },
    ref
  ) => {
    const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      if (!disabled) onSearch?.();
    };

    return (
      <form
        role="search"
        onSubmit={handleSubmit}
        className={cn("flex w-full min-w-0 items-stretch gap-2", formClassName)}
      >
        <div className="relative min-w-0 flex-1">
          <span
            className={cn(
              "pointer-events-none absolute top-1/2 z-[1] -translate-y-1/2 text-slate-400",
              searchBarIconLayout[barSize]
            )}
          >
            <Search className="shrink-0" />
          </span>
          <input
            ref={ref}
            type="search"
            disabled={disabled}
            placeholder={placeholder}
            autoComplete="off"
            {...inputProps}
            className={cn(
              FLOW_TEXT_INPUT_BASE,
              "rounded-md pr-3 shadow-none",
              searchBarTrackClasses[barSize],
              className
            )}
          />
        </div>
        {showSubmitButton ? (
          <StyleGuideButton
            type="submit"
            variant="nav"
            size={searchBarSubmitSize[barSize]}
            loading={submitLoading}
            disabled={disabled}
            className="shrink-0"
          >
            {submitLabel}
          </StyleGuideButton>
        ) : null}
      </form>
    );
  }
);
StyleGuideSearchBar.displayName = "StyleGuideSearchBar";

export type StyleGuideCardVariant = "default" | "elevated" | "ghost";

export interface StyleGuideCardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: StyleGuideCardVariant;
  padding?: StyleGuideComponentSize;
}

const cardVariantClasses: Record<StyleGuideCardVariant, string> = {
  default: "border border-slate-200 bg-white",
  elevated: "border border-slate-100 bg-white shadow-sm shadow-slate-900/[0.06]",
  ghost: "border border-transparent bg-slate-50",
};

const cardPaddingClasses: Record<StyleGuideComponentSize, string> = {
  sm: "p-3",
  md: "p-4",
  lg: "p-6",
};

export const StyleGuideCard: React.FC<StyleGuideCardProps> = ({
  variant = "default",
  padding = "md",
  className,
  children,
  ...rest
}) => (
  <div
    className={cn(
      "rounded-lg font-sans text-slate-800",
      cardVariantClasses[variant],
      cardPaddingClasses[padding],
      className
    )}
    {...rest}
  >
    {children}
  </div>
);

export type StyleGuideBadgeVariant =
  | "primary"
  | "secondary"
  | "success"
  | "error"
  | "ghost";

export interface StyleGuideBadgeProps extends React.HTMLAttributes<HTMLSpanElement> {
  variant?: StyleGuideBadgeVariant;
  size?: StyleGuideComponentSize;
}

const badgeVariantClasses: Record<StyleGuideBadgeVariant, string> = {
  primary: "bg-[#6EA5FF]/12 text-[#3d6fa8]",
  secondary: "bg-slate-100 text-slate-700",
  success: "bg-emerald-50 text-emerald-800",
  error: "bg-rose-50 text-rose-800",
  ghost: "bg-slate-50 text-slate-500",
};

const badgeSizeClasses: Record<StyleGuideComponentSize, string> = {
  sm: "px-2 py-0.5 text-[10px] font-medium",
  md: "px-2 py-0.5 text-xs font-medium",
  lg: "px-2.5 py-1 text-xs font-medium",
};

export const StyleGuideBadge: React.FC<StyleGuideBadgeProps> = ({
  variant = "primary",
  size = "md",
  className,
  children,
  ...rest
}) => (
  <span
    className={cn(
      "inline-flex items-center justify-center rounded-full font-medium",
      badgeVariantClasses[variant],
      badgeSizeClasses[size],
      className
    )}
    {...rest}
  >
    {children}
  </span>
);

function SectionShell(props: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): React.ReactElement {
  return (
    <section className="space-y-4">
      <header className="space-y-1 border-b border-slate-200 pb-3">
        <h2 className="text-lg font-semibold tracking-tight text-slate-900">
          {props.title}
        </h2>
        {props.description ? (
          <p className="text-sm text-slate-500">{props.description}</p>
        ) : null}
      </header>
      {props.children}
    </section>
  );
}

const StyleGuidePage: React.FC = () => {
  const { addToast } = useToast();
  const [demo, setDemo] = useState("");
  const [searchBarValue, setSearchBarValue] = useState("");
  const [searchBarLoading, setSearchBarLoading] = useState(false);
  const [wizardDemoStep, setWizardDemoStep] =
    useState<EditPropertyStepId>("description");
  const [radioDemo, setRadioDemo] = useState<"casa" | "apto" | "flat">("apto");
  const [textareaDemo, setTextareaDemo] = useState("");
  const [paginationDemoPage, setPaginationDemoPage] = useState(1);

  const handleCopyColorHex = useCallback(
    async (hex: string, label: string) => {
      try {
        await navigator.clipboard.writeText(hex);
        addToast({
          type: "success",
          title: `${label}: ${hex} copiado.`,
        });
      } catch {
        addToast({
          type: "error",
          title: "Não foi possível copiar. Verifica permissões do navegador.",
        });
      }
    },
    [addToast]
  );

  React.useEffect(() => {
    if (!searchBarLoading) return;
    const t = window.setTimeout(() => setSearchBarLoading(false), 1200);
    return () => window.clearTimeout(t);
  }, [searchBarLoading]);

  return (
    <div className="min-h-screen bg-[#F6F7F9] font-sans text-slate-800">
      <div className="mx-auto max-w-5xl space-y-12 px-4 py-10 sm:px-6 lg:px-8">
        <header className="space-y-2">
          <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
            ADM 7 Cantos
          </p>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Guia de estilo
          </h1>
        </header>

        <SectionShell title="Tokens visuais">
          <div className="grid gap-6 lg:grid-cols-2">
            <StyleGuideCard variant="default" padding="md">
              <div className="mb-3 space-y-1">
                <h3 className="text-sm font-semibold text-slate-800">Paleta</h3>
                <p className="text-[11px] leading-snug text-slate-500">
                  Clica numa amostra para copiar o valor hexadecimal.
                </p>
              </div>
              <div className="flex flex-wrap gap-3">
                {(
                  Object.entries(VISUAL_TOKENS.colors) as [
                    keyof typeof VISUAL_TOKENS.colors,
                    string
                  ][]
                ).map(([name, hex]) => {
                  const label = COLOR_SWATCH_LABELS[name];
                  return (
                    <div key={name} className="flex flex-col items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => void handleCopyColorHex(hex, label)}
                        className="h-12 w-12 rounded-md border border-slate-200 shadow-none outline-none ring-offset-2 transition hover:opacity-90 focus-visible:ring-2 focus-visible:ring-[#3182CE]/50"
                        style={{ backgroundColor: hex }}
                        title={`Copiar ${hex}`}
                        aria-label={`Copiar cor ${label}, ${hex}`}
                      />
                      <span className="max-w-[4.5rem] text-center text-[10px] font-medium uppercase text-slate-500">
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </StyleGuideCard>
            <StyleGuideCard variant="default" padding="md">
              <h3 className="mb-3 text-sm font-semibold text-slate-800">
                Escala tipográfica
              </h3>
              <pre className="max-h-48 overflow-auto rounded-md bg-slate-900 p-4 text-xs leading-relaxed text-emerald-100/90">
                {JSON.stringify(VISUAL_TOKENS.typography, null, 2)}
              </pre>
            </StyleGuideCard>
          </div>
        </SectionShell>

        <SectionShell title="Biblioteca interativa">
          <div className="space-y-10">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">Botão</h3>
              <StyleGuideCard variant="default" padding="md" className="space-y-4">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[520px] border-collapse text-left">
                    <thead>
                      <tr className="border-b border-slate-200 text-[10px] font-medium uppercase tracking-wide text-slate-500">
                        <th className="pb-2 pr-3 font-normal" />
                        <th className="pb-2 px-2 font-mono font-normal normal-case">
                          save → FLOW_SAVE_BUTTON_BASE
                        </th>
                        <th className="pb-2 px-2 font-mono font-normal normal-case">
                          action → FLOW_ORANGE_ACTION_BUTTON_BASE
                        </th>
                        <th className="pb-2 px-2 font-mono font-normal normal-case">
                          nav → FLOW_PRIMARY_BUTTON_BASE
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {(["sm", "md", "lg"] as const).map((sz) => (
                        <tr key={sz} className="border-b border-slate-100">
                          <td className="py-3 pr-3 font-mono text-xs text-slate-400">
                            {sz}
                          </td>
                          <td className="p-2 align-middle">
                            <StyleGuideButton variant="save" size={sz} type="button">
                              save
                            </StyleGuideButton>
                          </td>
                          <td className="p-2 align-middle">
                            <StyleGuideButton variant="action" size={sz} type="button">
                              action
                            </StyleGuideButton>
                          </td>
                          <td className="p-2 align-middle">
                            <StyleGuideButton variant="nav" size={sz} type="button">
                              nav
                            </StyleGuideButton>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <p className="break-all font-mono text-[10px] leading-snug text-slate-500">
                  {FLOW_BUTTON_SOLID_SHELL}
                </p>
              </StyleGuideCard>
            </div>

            <StyleGuideCard variant="default" padding="md" className="max-w-3xl space-y-10">
              <div className="space-y-3">
                <p className="font-mono text-[10px] text-slate-500">
                  FLOW_TEXT_INPUT_BASE
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <StyleGuideInput
                    label="Text"
                    type="text"
                    placeholder="…"
                    value={demo}
                    onChange={(e) => setDemo(e.target.value)}
                  />
                  <StyleGuideInput
                    label="Email"
                    type="email"
                    placeholder="email@…"
                    leftIcon={<Mail className="h-4 w-4" />}
                  />
                  <StyleGuideInput
                    label="Tel"
                    type="tel"
                    placeholder="+351 …"
                  />
                </div>
                <p className={FLOW_FORM_HELPER_TEXT_BASE}>
                  * Digite ao menos 4 caracteres e pressione enter para pesquisar
                </p>
                <p className="pt-2 font-mono text-[10px] text-slate-500">
                  Estados (foco via Tab · erro · sucesso)
                </p>
                <div className="grid gap-4 sm:grid-cols-3">
                  <StyleGuideInput
                    label="Foco"
                    type="text"
                    defaultValue="Tab aqui"
                    helperText="Borda e anel azul no foco."
                  />
                  <StyleGuideInput
                    label="Erro"
                    type="text"
                    defaultValue="000"
                    validationState="error"
                    helperText="Formato inválido."
                  />
                  <StyleGuideInput
                    label="Sucesso"
                    type="text"
                    defaultValue="ok@exemplo.pt"
                    validationState="success"
                    helperText="Valor aceite."
                  />
                </div>
              </div>
              <div className="space-y-3">
                <p className="font-mono text-[10px] text-slate-500">
                  FLOW_TEXTAREA_BASE
                </p>
                <StyleGuideTextarea
                  label="Textarea"
                  value={textareaDemo}
                  onChange={(e) => setTextareaDemo(e.target.value)}
                  rows={3}
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <StyleGuideTextarea
                    label="Textarea · erro"
                    validationState="error"
                    defaultValue="Texto"
                    rows={2}
                    helperText="Revise o conteúdo antes de guardar."
                  />
                  <StyleGuideTextarea
                    label="Textarea · sucesso"
                    validationState="success"
                    defaultValue="Descrição completa."
                    rows={2}
                    helperText="Guardado com sucesso."
                  />
                </div>
              </div>
              <div className="space-y-3">
                <p className="font-mono text-[10px] text-slate-500">
                  {`radio + ${FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS}`}
                </p>
                <div className="flex flex-wrap gap-6">
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="styleguide-property-type"
                      checked={radioDemo === "casa"}
                      onChange={() => setRadioDemo("casa")}
                      className={cn(
                        "h-4 w-4 shrink-0",
                        FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS
                      )}
                    />
                    <span className="text-sm font-normal text-[#2D3748]">Casa</span>
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="styleguide-property-type"
                      checked={radioDemo === "apto"}
                      onChange={() => setRadioDemo("apto")}
                      className={cn(
                        "h-4 w-4 shrink-0",
                        FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS
                      )}
                    />
                    <span className="text-sm font-normal text-[#2D3748]">
                      Apartamento
                    </span>
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2">
                    <input
                      type="radio"
                      name="styleguide-property-type"
                      checked={radioDemo === "flat"}
                      onChange={() => setRadioDemo("flat")}
                      className={cn(
                        "h-4 w-4 shrink-0",
                        FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS
                      )}
                    />
                    <span className="text-sm font-normal text-[#2D3748]">Flat</span>
                  </label>
                </div>
              </div>
              <div className="space-y-3">
                <p className="font-mono text-[10px] text-slate-500">
                  {`checkbox + border-[#E2E8F0] + ${FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS}`}
                </p>
                <label className="inline-flex cursor-pointer items-center gap-2">
                  <input
                    type="checkbox"
                    defaultChecked
                    className={cn(
                      "h-4 w-4 shrink-0 rounded-md border border-[#E2E8F0]",
                      FLOW_FORM_NATIVE_CONTROL_ACCENT_CLASS
                    )}
                  />
                  <span className="text-sm font-normal text-[#2D3748]">Checkbox</span>
                </label>
              </div>
            </StyleGuideCard>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">
                Barra de pesquisa
              </h3>
              <div className="max-w-xl">
                <SearchBarToHeader
                  formClassName="w-full"
                  value={searchBarValue}
                  onChange={(e) => setSearchBarValue(e.target.value)}
                  onSearch={() => setSearchBarLoading(true)}
                  submitLoading={searchBarLoading}
                  submitLabel="Pesquisar"
                  placeholder="Pesquisar imóveis"
                />
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">Cartão</h3>
              <div className="grid gap-4 md:grid-cols-3">
                <StyleGuideCard variant="default">
                  <p className="text-sm font-medium text-slate-800">Padrão</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Borda neutra, fundo branco.
                  </p>
                </StyleGuideCard>
                <StyleGuideCard variant="elevated">
                  <p className="text-sm font-medium text-slate-800">Elevado</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Sombra discreta.
                  </p>
                </StyleGuideCard>
                <StyleGuideCard variant="ghost">
                  <p className="text-sm font-medium text-slate-800">Neutro</p>
                  <p className="mt-1 text-xs text-slate-500">
                    Fundo cinza-claro.
                  </p>
                </StyleGuideCard>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">Etiqueta</h3>
              <div className="flex flex-wrap gap-2">
                <StyleGuideBadge variant="primary">Primário</StyleGuideBadge>
                <StyleGuideBadge variant="secondary">Secundário</StyleGuideBadge>
                <StyleGuideBadge variant="success">
                  <Check className="mr-1 h-3 w-3" />
                  Sucesso
                </StyleGuideBadge>
                <StyleGuideBadge variant="error">
                  <X className="mr-1 h-3 w-3" />
                  Erro
                </StyleGuideBadge>
                <StyleGuideBadge variant="ghost">Neutro</StyleGuideBadge>
              </div>
            </div>
          </div>
        </SectionShell>

        <SectionShell title="Iconografia (react-icons/fi)">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6">
              {LUCIDE_ICON_CATALOG.map(({ name, Icon }) => (
                <div
                  key={name}
                  className="flex flex-col items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-4 text-center transition hover:border-slate-300 hover:bg-slate-50/80"
                >
                  <Icon className="h-5 w-5 shrink-0 text-slate-600" aria-hidden />
                  <span className="font-mono text-[10px] font-medium leading-tight text-slate-500">
                    {name}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </SectionShell>

        <SectionShell title="Passos, raios e escolha única">
          <div className="space-y-8">
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">
                Indicadores redondos (passos)
              </h3>
              <WizardSteps
                items={EDIT_STEPS.map((step) => ({
                  id: step.id,
                  label: step.label,
                }))}
                activeId={wizardDemoStep}
                isUnlocked={() => true}
                onSelect={(id) => setWizardDemoStep(id as EditPropertyStepId)}
              />
            </div>

            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-slate-800">
                Raios (border-radius)
              </h3>
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {RADIUS_CATALOG.map((row) => (
                  <StyleGuideCard key={row.token} variant="default" padding="sm">
                    <div
                      className={cn(
                        "mb-3 h-14 w-full border-2 border-dashed border-slate-200 bg-slate-50",
                        row.token
                      )}
                    />
                    <p className="font-mono text-xs font-semibold text-slate-800">
                      {row.token}
                    </p>
                    <p className="mt-1 text-[11px] leading-snug text-slate-500">
                      {row.usage}
                    </p>
                  </StyleGuideCard>
                ))}
              </div>
            </div>

          </div>
        </SectionShell>

        <SectionShell title="Padrões de layout">
          <StyleGuideCard variant="default" padding="lg" className="space-y-6">
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Grelha de 12 colunas (visual)
              </p>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-12">
                {Array.from({ length: 12 }).map((_, i) => (
                  <div
                    key={i}
                    className="flex h-10 items-center justify-center rounded bg-[#6EA5FF]/15 text-[10px] font-medium text-slate-700"
                  >
                    {i + 1}
                  </div>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Pilha vertical (space-y-4)
              </p>
              <div className="space-y-4 rounded-md border border-dashed border-slate-300 p-4">
                <div className="h-2 rounded bg-slate-300" />
                <div className="h-2 rounded bg-slate-300" />
                <div className="h-2 rounded bg-slate-300" />
              </div>
            </div>
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Área dividida (destaque + apoio)
              </p>
              <div className="overflow-hidden rounded-md border border-slate-200">
                <div className="grid grid-cols-1 divide-y divide-slate-200 md:grid-cols-12 md:divide-x md:divide-y-0">
                  <div className="bg-white p-5 md:col-span-7">
                    <p className="text-sm font-medium text-slate-800">
                      Painel principal
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                      Grelha aproximada de 7/12 em desktop: formulário, detalhe ou
                      lista principal.
                    </p>
                  </div>
                  <div className="bg-slate-50/90 p-5 md:col-span-5">
                    <p className="text-sm font-medium text-slate-800">
                      Painel de apoio
                    </p>
                    <p className="mt-1.5 text-xs leading-relaxed text-slate-500">
                      Grelha aproximada de 5/12: resumo, metadados ou ações
                      secundárias.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div>
              <p className="mb-4 text-xs font-semibold uppercase tracking-wide text-slate-500">
                Sumário (paginação)
              </p>
              <PaginationBar
                currentPage={paginationDemoPage}
                totalPages={12}
                onPageChange={setPaginationDemoPage}
              />
              <p className="mt-3 text-xs text-slate-500">
                Página atual (demo):{" "}
                <span className="font-mono text-slate-700">{paginationDemoPage}</span>
              </p>
            </div>
          </StyleGuideCard>
        </SectionShell>
      </div>
    </div>
  );
};

export default StyleGuidePage;
